/**
 * Default catalogue for IO Services. Mirrors
 * `scripts/seed-platform-services.ts` but runs from the client
 * via the admin's authenticated Firebase session — no gcloud ADC
 * needed.
 *
 * Idempotent: each entry carries a stable `seedKey`. Re-running
 * UPDATES existing seeded docs (matched by seedKey) and inserts
 * any missing ones; existing admin edits to other fields persist.
 *
 * Flagships (Floor Protection, Pest Control) carry 5 placeholder
 * images + 6 features + price note. The other 17 entries are
 * stubs with 2 images + tagline + 3 features that the admin
 * curates via the catalogue editor.
 *
 * Placeholder images are Picsum URLs seeded per-service — they're
 * random landscape photos, not real service photos. Replace with
 * marketing images via the catalogue edit form.
 */
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentReference,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import type { PlatformServiceCategory } from '@/lib/platformServices';

type SeedEntry = {
  seedKey: string;
  title: string;
  category: PlatformServiceCategory;
  tagline: string;
  shortDescription: string;
  longDescription?: string;
  priceLabel: string;
  priceNote?: string;
  deliveryTime?: string;
  features: string[];
  images: string[];
  sortOrder: number;
};

function img(seed: string, n: number): string {
  return `https://picsum.photos/seed/io-${seed}-${n}/800/600`;
}

export const DEFAULT_PLATFORM_SERVICES: SeedEntry[] = [
  // ── Site execution ────────────────────────────────────────
  {
    seedKey: 'site.floor_protection',
    title: 'Floor Protection',
    category: 'site',
    tagline: 'One call. We take care of everything — material, install, removal.',
    shortDescription:
      'Heavy-duty floor protection sheets installed across your finished marble, hardwood, tile, and laminate floors so the rest of the work never leaves a scratch.',
    longDescription:
      'Our crew arrives on site with everything: rolls of premium protection sheets, foam underlay for tile, corner + edge guards, and the tape we trust on luxury finishes. We measure, lay, tape down seams, and tag every door + access point. At handover we strip everything and haul the waste — your client walks into a perfect floor.',
    priceLabel: '₹9 / sqft',
    priceNote: 'Material + installation included',
    deliveryTime: 'Within 24 hrs',
    features: [
      'Free site measurement on the same day you call',
      'Premium 200-micron protection — handles foot traffic, ladders, paint drops',
      'Corner + edge guards for skirting and door frames',
      'Site engineer supervises layout to your floor plan',
      'Removed + hauled away on handover day (no waste left for you)',
      '12-month replacement warranty on punctures or tears',
    ],
    images: [
      img('floor-protection', 1),
      img('floor-protection', 2),
      img('floor-protection', 3),
      img('floor-protection', 4),
      img('floor-protection', 5),
    ],
    sortOrder: 10,
  },
  {
    seedKey: 'site.pest_control',
    title: 'Pest Control',
    category: 'site',
    tagline: 'Licensed crew. Odourless chemicals. 12-month warranty.',
    shortDescription:
      'Pre-handover anti-termite, cockroach, and rodent treatment by a licensed pest control crew. Government-approved chemicals only — safe for kids and pets.',
    longDescription:
      'Comprehensive site treatment before client handover. Includes anti-termite spraying along skirting + plinth, cockroach gel in kitchen + bathroom cabinets, rodent stations near service shafts, and a 12-month warranty with free re-visits if any infestation reappears.',
    priceLabel: 'Starting ₹3,500',
    priceNote: 'Per visit · Up to 2 BHK · Add-ons on request',
    deliveryTime: '2–3 days',
    features: [
      'Government-licensed pest control operator',
      'Odourless chemicals — client can move in same day',
      'Anti-termite + cockroach + rodent in one visit',
      'Treatment certificate handed to your client',
      '12-month warranty with free re-visits',
      'Kid + pet safe formulations available on request',
    ],
    images: [
      img('pest-control', 1),
      img('pest-control', 2),
      img('pest-control', 3),
      img('pest-control', 4),
      img('pest-control', 5),
    ],
    sortOrder: 20,
  },
  {
    seedKey: 'site.deep_cleaning',
    title: 'Deep Cleaning',
    category: 'site',
    tagline: 'Hand-over ready in one day.',
    shortDescription:
      'Professional handover cleaning — every surface, fixture, and corner buffed for the client walk-in.',
    priceLabel: 'Starting ₹6 / sqft',
    priceNote: 'Material + crew included',
    deliveryTime: '1 day',
    features: [
      'Window + glass polish',
      'Bathroom de-grease + de-scale',
      'Floor scrub + buff to a shine',
    ],
    images: [img('deep-cleaning', 1), img('deep-cleaning', 2)],
    sortOrder: 30,
  },
  {
    seedKey: 'site.polishing',
    title: 'Polishing & Buffing',
    category: 'site',
    tagline: 'Showroom finish on marble, granite, and wood.',
    shortDescription:
      'Diamond polish for stone floors, PU coat for wooden floors. Sample patch always done first so you can approve the finish.',
    priceLabel: 'On request',
    deliveryTime: '2–4 days',
    features: [
      'Diamond pad polish for marble + granite',
      'PU / wax coat options for hardwood',
      'Sample patch approval before full job',
    ],
    images: [img('polishing', 1), img('polishing', 2)],
    sortOrder: 40,
  },
  {
    seedKey: 'site.ac_install',
    title: 'AC Installation',
    category: 'site',
    tagline: 'Brand-agnostic install with copper piping + electrical.',
    shortDescription:
      'Split / cassette AC installation with copper piping, drain line, and stabilizer wiring. Brand-agnostic — we install what you spec.',
    priceLabel: 'Starting ₹2,500 / indoor unit',
    priceNote: 'Copper + drain + electrical included',
    deliveryTime: 'Within 48 hrs',
    features: [
      'Copper pipe + insulated drain line',
      'Stabilizer wiring at the indoor unit',
      'Test run + handover demo with client',
    ],
    images: [img('ac-install', 1), img('ac-install', 2)],
    sortOrder: 50,
  },
  {
    seedKey: 'site.smart_home',
    title: 'Smart Home Setup',
    category: 'site',
    tagline: 'Lighting, voice, security — programmed and demoed.',
    shortDescription:
      'Smart switches, scene programming, voice control pairing, and security camera setup. Client walkthrough included.',
    priceLabel: 'On request',
    deliveryTime: '3–5 days',
    features: [
      'Alexa / Google Home pairing',
      'Scene programming for living / dining / master',
      'Client walkthrough + cheat sheet handed over',
    ],
    images: [img('smart-home', 1), img('smart-home', 2)],
    sortOrder: 60,
  },

  // ── Marketing & branding ──────────────────────────────────
  {
    seedKey: 'mkt.landing_page',
    title: 'Website / Landing Page',
    category: 'marketing',
    tagline: 'Studio portfolio that turns visitors into leads.',
    shortDescription:
      'Conversion-optimised single page or full studio website. Copywriting, photo gallery, contact form, WhatsApp chat, and Google Maps — all wired up.',
    longDescription:
      'We build, copy, host, and hand you the keys. Mobile-first design, custom domain ready, hosted free for the first year, lead form pipes straight to your inbox.',
    priceLabel: 'Starting ₹25,000',
    priceNote: 'One-time · Includes first-year hosting',
    deliveryTime: '7–10 days',
    features: [
      'Mobile-first design with your brand colours',
      'Lead form → straight to your inbox + WhatsApp',
      'Google indexing + sitemap submission included',
      'Free hosting for the first year',
    ],
    images: [img('landing', 1), img('landing', 2)],
    sortOrder: 10,
  },
  {
    seedKey: 'mkt.logo_brand',
    title: 'Logo + Brand Identity',
    category: 'marketing',
    tagline: 'Full identity kit — logo, palette, business card, letterhead.',
    shortDescription:
      'Logo, colour palette, type system, business card, and letterhead. Three concept rounds. All file formats handed over.',
    priceLabel: 'Starting ₹15,000',
    priceNote: '3 concept rounds · All file formats',
    deliveryTime: '5–7 days',
    features: ['3 concept rounds', 'All file formats (AI, PSD, PNG, SVG)', 'Brand guidelines PDF'],
    images: [img('logo', 1), img('logo', 2)],
    sortOrder: 20,
  },
  {
    seedKey: 'mkt.portfolio_shoot',
    title: 'Portfolio Photography',
    category: 'marketing',
    tagline: 'Instagram-ready interior shoot of your completed project.',
    shortDescription:
      'Professional interior photographer + 25+ edited photos. Wide + detail shots. Both landscape + square crops.',
    priceLabel: 'Starting ₹15,000 / project',
    priceNote: 'Per shoot · 25+ edited photos',
    deliveryTime: 'Same week',
    features: ['25+ edited photos', 'Wide + detail shots', 'Landscape + square crops'],
    images: [img('shoot', 1), img('shoot', 2)],
    sortOrder: 30,
  },
  {
    seedKey: 'mkt.drone_shoot',
    title: 'Drone / Exterior Shoot',
    category: 'marketing',
    tagline: 'Aerial + exterior footage for villas and large sites.',
    shortDescription:
      'Licensed drone operator. 4K footage + stills. Edited 30-sec reel included.',
    priceLabel: 'Starting ₹8,000',
    priceNote: 'Per shoot · Reel included',
    deliveryTime: 'Same week',
    features: ['Licensed drone operator', '4K footage + stills', 'Edited 30-sec reel'],
    images: [img('drone', 1), img('drone', 2)],
    sortOrder: 40,
  },
  {
    seedKey: 'mkt.3d_walkthrough',
    title: '3D Walkthrough Video',
    category: 'marketing',
    tagline: 'Animated walkthrough for client presentation + reels.',
    shortDescription:
      '60–90 sec animated 3D walkthrough of your design. Royalty-free music. Vertical + horizontal cuts.',
    priceLabel: 'Starting ₹20,000',
    priceNote: 'Per project · 2 cuts included',
    deliveryTime: '7–10 days',
    features: ['60–90 sec walkthrough', 'Royalty-free music', 'Vertical + horizontal cuts'],
    images: [img('3d', 1), img('3d', 2)],
    sortOrder: 50,
  },
  {
    seedKey: 'mkt.social_media',
    title: 'Social Media Management',
    category: 'marketing',
    tagline: '12 posts + 4 reels every month. Hands-off for you.',
    shortDescription:
      'Monthly Instagram + Facebook content. Posts, reels, captions, hashtags, monthly insights report.',
    priceLabel: 'Starting ₹12,000 / month',
    priceNote: 'Monthly · 12 posts + 4 reels',
    deliveryTime: 'Ongoing',
    features: ['12 posts + 4 reels / month', 'Caption + hashtag research', 'Monthly insights report'],
    images: [img('social', 1), img('social', 2)],
    sortOrder: 60,
  },
  {
    seedKey: 'mkt.google_business',
    title: 'Google Business Listing',
    category: 'marketing',
    tagline: 'Show up on Maps when clients search for designers.',
    shortDescription:
      'Verified Google Business profile setup. Photos, services, and review request templates handed over.',
    priceLabel: '₹3,500',
    priceNote: 'One-time · Verification included',
    deliveryTime: '1–2 days',
    features: ['Verification done for you', 'Photos + services added', 'Review request templates'],
    images: [img('gbiz', 1), img('gbiz', 2)],
    sortOrder: 70,
  },

  // ── Business ops ──────────────────────────────────────────
  {
    seedKey: 'biz.gst_accounting',
    title: 'GST Filing + Accounting',
    category: 'business',
    tagline: 'Monthly returns + bookkeeping by a chartered accountant.',
    shortDescription:
      'CA-led GST filing + bookkeeping. Per-project P&L on request. WhatsApp support for tax questions.',
    priceLabel: 'Starting ₹3,500 / month',
    priceNote: 'Monthly · Chartered Accountant included',
    deliveryTime: 'Ongoing',
    features: ['Monthly GST returns', 'Profit & loss statement', 'WhatsApp support'],
    images: [img('gst', 1), img('gst', 2)],
    sortOrder: 10,
  },
  {
    seedKey: 'biz.legal_contracts',
    title: 'Legal Contract Templates',
    category: 'business',
    tagline: 'Lawyer-drafted agreements customised to your studio.',
    shortDescription:
      'Client design agreement, vendor MoU, NDA — drafted by a lawyer, customised for your studio name + standard terms.',
    priceLabel: 'Starting ₹4,500',
    priceNote: 'One-time · Customised to your studio',
    deliveryTime: '3–5 days',
    features: ['Client design agreement', 'Vendor MoU', 'NDA template'],
    images: [img('legal', 1), img('legal', 2)],
    sortOrder: 20,
  },
  {
    seedKey: 'biz.domain_email',
    title: 'Custom Domain Email',
    category: 'business',
    tagline: 'studio@yourbrand.com on Google Workspace.',
    shortDescription:
      'Set up Google Workspace on your custom domain. Up to 5 mailboxes, calendar + Drive included, mobile setup support.',
    priceLabel: '₹1,500 setup + ₹150 / user / month',
    priceNote: 'Setup one-time + monthly per user',
    deliveryTime: '1 day',
    features: ['Up to 5 mailboxes', 'Calendar + Drive included', 'Mobile setup help'],
    images: [img('email', 1), img('email', 2)],
    sortOrder: 30,
  },

  // ── Materials & vendors ───────────────────────────────────
  {
    seedKey: 'mat.logistics',
    title: 'Material Delivery (Last-mile)',
    category: 'materials',
    tagline: 'Same-day pickup + delivery. Loading crew included.',
    shortDescription:
      'Same-day material pickup from your vendor and delivery to site. Tempo / pickup options. Loading + unloading crew included. GPS tracked.',
    priceLabel: 'Starting ₹600 / trip',
    priceNote: 'Per trip · Loading + unloading included',
    deliveryTime: 'Same day',
    features: ['Tempo / Pickup options', 'Loading crew included', 'GPS tracked end-to-end'],
    images: [img('logistics', 1), img('logistics', 2)],
    sortOrder: 10,
  },
  {
    seedKey: 'mat.sample_procurement',
    title: 'Sample Procurement',
    category: 'materials',
    tagline: 'Up to 10 samples per run, delivered to your studio.',
    shortDescription:
      'Source laminate / fabric / tile samples from multiple vendors and deliver to your studio with a comparison sheet.',
    priceLabel: '₹500 / sample run',
    priceNote: 'Up to 10 samples · Returns accepted',
    deliveryTime: '2–3 days',
    features: ['Up to 10 samples per run', 'Source comparison table', 'Returns accepted'],
    images: [img('samples', 1), img('samples', 2)],
    sortOrder: 20,
  },
  {
    seedKey: 'mat.vendor_verification',
    title: 'Vendor Verification',
    category: 'materials',
    tagline: 'Background check before you place a large order.',
    shortDescription:
      'GST + PAN check, reference calls, prior project photos. Done before you place a large order with a new vendor.',
    priceLabel: '₹1,500 / vendor',
    priceNote: 'Per vendor · Report in 2 days',
    deliveryTime: '2 days',
    features: ['GST + PAN check', 'Reference calls', 'Prior project photos'],
    images: [img('vendor', 1), img('vendor', 2)],
    sortOrder: 30,
  },
];

export type SeedDefaultsResult = {
  created: number;
  updated: number;
  total: number;
};

/**
 * Upsert the default catalogue into Firestore. Caller must be an
 * `app_owner` — Firestore rules reject writes from anyone else.
 *
 * Strategy: query existing docs that carry a `seedKey`, build a
 * lookup map, then for each entry in `DEFAULT_PLATFORM_SERVICES`
 * either UPDATE the existing doc (matched by seedKey) or ADD a
 * new doc. Idempotent — safe to re-run any time.
 *
 * @param createdByUid The current user's uid to stamp on new docs.
 */
export async function seedDefaultPlatformServices(
  createdByUid: string,
): Promise<SeedDefaultsResult> {
  // Read existing seeded docs so we can upsert.
  const existingSnap = await getDocs(
    query(collection(db, 'platformServices'), where('seedKey', '!=', null)),
  );
  const bySeed = new Map<string, DocumentReference>();
  existingSnap.forEach((d) => {
    const k = d.get('seedKey');
    if (typeof k === 'string') bySeed.set(k, d.ref);
  });

  let created = 0;
  let updated = 0;
  const col = collection(db, 'platformServices');

  for (const entry of DEFAULT_PLATFORM_SERVICES) {
    const payload: Record<string, unknown> = {
      seedKey: entry.seedKey,
      title: entry.title,
      category: entry.category,
      tagline: entry.tagline,
      shortDescription: entry.shortDescription,
      priceLabel: entry.priceLabel,
      features: entry.features,
      images: entry.images,
      sortOrder: entry.sortOrder,
      isActive: true,
      updatedAt: serverTimestamp(),
    };
    if (entry.longDescription) payload.longDescription = entry.longDescription;
    if (entry.priceNote) payload.priceNote = entry.priceNote;
    if (entry.deliveryTime) payload.deliveryTime = entry.deliveryTime;

    const existingRef = bySeed.get(entry.seedKey);
    if (existingRef) {
      await updateDoc(existingRef, payload);
      updated++;
    } else {
      payload.createdBy = createdByUid;
      payload.createdAt = serverTimestamp();
      await addDoc(col, payload);
      created++;
    }
  }

  return { created, updated, total: DEFAULT_PLATFORM_SERVICES.length };
}
